/**
 * Modulo Front - Gestione funzionalità generali del frontend
 */
export class Front {
  constructor() {
    console.log("Front module instantiated")
  }

  /**
   * Inizializza il modulo
   */
  public init(): void {
    console.log("Front module initialized")
    this.setupEventListeners()
  }

  /**
   * Configura gli event listener generali
   */
  private setupEventListeners(): void {
    // Event listener di esempio per click su elementi con classe .front-button
    document.addEventListener("click", event => {
      const target = event.target as HTMLElement
      // Gestione click su "Download PDF" (supporto click su icona interna)
      const pdfButton = target.closest(".btn-generateFpiMemO28Document") as HTMLElement
      if (pdfButton) {
        console.log("Generate FPI MEMO 028 Document button clicked")
        const jsonDataAttr = pdfButton.getAttribute("data-json")
        if (jsonDataAttr) {
          try {
            const jsonData = JSON.parse(jsonDataAttr)
            this.generateAndDownloadPDF(jsonData, pdfButton)
          } catch (error) {
            console.error("Error parsing JSON data:", error)
          }
        } else {
          console.error("No data-json attribute found on the button.")
        }
        return
      }

      // Gestione click su "Delete Form"
      const deleteButton = target.closest(".btn-deleteFpiMemO28Form") as HTMLElement
      if (deleteButton) {
        const formId = deleteButton.getAttribute("data-form-id")
        if (formId) {
          this.confirmDeleteForm(formId, deleteButton)
        }
      }
    })
  }

  /**
   * Mostra modal di conferma e poi elimina il form
   */
  private confirmDeleteForm(formId: string, buttonElement: HTMLElement): void {
    const bootbox = (window as any).bootbox
    if (!bootbox) {
      console.error("Bootbox not available")
      return
    }

    bootbox.confirm({
      title: "Delete Form",
      message: "Are you sure you want to delete this form? This action cannot be undone.",
      buttons: {
        cancel: {
          label: "Cancel",
          className: "btn-default"
        },
        confirm: {
          label: "Delete",
          className: "btn-danger"
        }
      },
      callback: (result: boolean) => {
        if (result) {
          this.deleteFpiMem028Form(formId, buttonElement)
        }
      }
    })
  }

  /**
   * Elimina un form FPI MEM028
   */
  private deleteFpiMem028Form(formId: string, buttonElement: HTMLElement): void {
    const $ = (window as any).jQuery
    const button = buttonElement as HTMLButtonElement
    button.disabled = true

    $.ajax({
      url: `${(window as any).CI.base_url}front/fpi_mem028/delete_form`,
      type: "POST",
      contentType: "application/json",
      data: JSON.stringify({ form_id: formId }),
      dataType: "json",
      success: (response: any) => {
        if (response.success) {
          window.location.reload()
        } else {
          alert(response.message || "Error deleting form")
          button.disabled = false
        }
      },
      error: (xhr: any) => {
        console.error("Error deleting form:", xhr.responseText)
        alert("Error deleting form")
        button.disabled = false
      }
    })
  }

  /**
   * Ottiene l'URL base dell'API dal meta tag (sicuro)
   */
  private getApiBaseUrl(): string {
    const metaTag = document.querySelector('meta[name="api-base-url"]') as HTMLMetaElement
    return metaTag?.content || ""
  }

  /**
   * Genero e scarico il documento PDF chiamando l'API
   */
  private async generateAndDownloadPDF(jsonData: any, buttonElement: HTMLElement): Promise<void> {
    // Cast dell'elemento per accedere alle proprietà del button
    const button = buttonElement as HTMLButtonElement
    const originalText = button.textContent || "Generate PDF"
    try {
      // Disabilito il pulsante durante la generazione
      button.disabled = true
      button.textContent = "Please wait..."

      // Uso la configurazione sicura dal meta tag
      const apiUrl = `${this.getApiBaseUrl()}/app/generate-pdf`

      // Chiamo l'API usando axios
      const response = await (window as any).axios.post(apiUrl, jsonData, {
        responseType: "blob", // Importante per gestire i file binari
        headers: {
          "Content-Type": "application/json",
          Accept: "application/pdf"
        },
        withCredentials: false, // Importante per CORS
        timeout: 30000 // 30 secondi di timeout
      })

      console.log("PDF generation successful")

      // Creo un URL temporaneo per il blob del PDF
      const pdfBlob = new Blob([response.data], { type: "application/pdf" })
      const pdfUrl = window.URL.createObjectURL(pdfBlob)

      // Creo un link temporaneo per scaricare il file
      const downloadLink = document.createElement("a")
      downloadLink.href = pdfUrl
      downloadLink.download = `fpi_mem028_${new Date().getTime()}.pdf`

      // Aggiungo il link al DOM, faccio click e lo rimuovo
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)

      // Pulisco l'URL temporaneo
      window.URL.revokeObjectURL(pdfUrl)

      console.log("PDF downloaded successfully")
    } catch (error) {
      console.error("Error generating PDF:", error)
      console.error("Error details:", {
        message: (error as any).message,
        code: (error as any).code,
        response: (error as any).response,
        request: (error as any).request
      })

      // Gestisco diversi tipi di errore
      let errorMessage = "Errore durante la generazione del PDF"

      if ((error as any).code === "ERR_NETWORK") {
        errorMessage = "Errore di rete - Impossibile raggiungere il server API"
      } else if ((error as any).response) {
        // Errore dalla risposta del server
        const status = (error as any).response.status
        const statusText = (error as any).response.statusText
        errorMessage = `Errore ${status}: ${statusText}`

        // Se il server restituisce un JSON con un messaggio di errore
        try {
          const errorData = (error as any).response.data
          if (errorData && errorData.error) {
            errorMessage = errorData.error
          }
        } catch (parseError) {
          // Ignoro errori di parsing
        }
      } else if ((error as any).request) {
        // Errore di rete
        errorMessage = "Errore di connessione al server - Controllare che l'API sia in esecuzione"
      }

      // Mostro l'errore all'utente
      alert(errorMessage)
    } finally {
      // Riabilito il pulsante
      const button = buttonElement as HTMLButtonElement
      button.disabled = false
      button.textContent = originalText
    }
  }
}
